export type CertificateFormat = 'letter' | 'linkedin' | 'instagram'

export type CertificateFontStack = 'name' | 'serif' | 'serifBold' | 'sans'

export type CertificateTextAlign = 'left' | 'center' | 'right'

export type CertificateLineKey =
  | 'learnerName'
  | 'courseName'
  | 'completionDate'
  | 'detailsLetter'
  | 'detailsSocial'
  | 'credential'
  | 'verifyLetter'
  | 'verifyShort'
  | 'credentialAndVerify'

export type CertificateLines = Partial<Record<CertificateLineKey, string>>

export type CertificateField = {
  line: CertificateLineKey
  x: number
  y: number
  width: number
  height: number
  align: CertificateTextAlign
  font: CertificateFontStack
  size: number
  minSize: number
  color: string
  maxLines?: number
  maxWidth?: number
  clearBackground?: boolean
  flowAfterLine?: CertificateLineKey
}

export type CertificateFormatLayout = {
  width: number
  height: number
  defaultScale: number
  background: string
  fields: CertificateField[]
}

export type CertificateLayout = {
  paperColor: string
  formats: Record<CertificateFormat, CertificateFormatLayout>
}

const INK = '#22201D'
const NAME_RED = '#8E2327'
const SMALL_PRINT = '#4F4A42'

const AASA_2026: CertificateLayout = {
  paperColor: '#FBF8F0',
  formats: {
    letter: {
      width: 1056,
      height: 816,
      defaultScale: 2,
      background: 'certificates/aasa-2026/letter-blank.png',
      fields: [
        {
          line: 'learnerName',
          x: 97,
          y: 344.7,
          width: 862,
          height: 80,
          align: 'center',
          font: 'name',
          size: 68,
          minSize: 40,
          color: NAME_RED,
        },
        {
          line: 'courseName',
          x: 97,
          y: 471.7,
          width: 862,
          height: 36,
          align: 'center',
          font: 'serifBold',
          size: 28,
          minSize: 22,
          color: INK,
          maxLines: 2,
        },
        {
          line: 'detailsLetter',
          x: 97,
          y: 513.7,
          width: 862,
          height: 22,
          align: 'center',
          font: 'serif',
          size: 15,
          minSize: 12,
          color: INK,
          flowAfterLine: 'courseName',
        },
        {
          line: 'completionDate',
          x: 693,
          y: 600.3,
          width: 266,
          height: 22,
          align: 'center',
          font: 'serif',
          size: 15,
          minSize: 12,
          color: INK,
        },
        {
          line: 'credential',
          x: 693,
          y: 658.3,
          width: 266,
          height: 18,
          align: 'center',
          font: 'serif',
          size: 12,
          minSize: 9,
          color: SMALL_PRINT,
        },
        {
          line: 'verifyLetter',
          x: 97,
          y: 728,
          width: 862,
          height: 16,
          align: 'center',
          font: 'sans',
          size: 12,
          minSize: 9,
          color: SMALL_PRINT,
          clearBackground: true,
        },
      ],
    },
    linkedin: {
      width: 1200,
      height: 627,
      defaultScale: 2,
      background: 'certificates/aasa-2026/linkedin-blank.png',
      fields: [
        {
          line: 'learnerName',
          x: 85,
          y: 316.5,
          width: 1030,
          height: 78,
          align: 'center',
          font: 'name',
          size: 68,
          minSize: 40,
          color: NAME_RED,
        },
        {
          line: 'courseName',
          x: 85,
          y: 411.5,
          width: 1030,
          height: 36,
          align: 'center',
          font: 'serifBold',
          size: 28,
          minSize: 22,
          color: INK,
          maxLines: 2,
        },
        {
          line: 'detailsSocial',
          x: 85,
          y: 451.5,
          width: 1030,
          height: 24,
          align: 'center',
          font: 'serif',
          size: 16,
          minSize: 12,
          color: INK,
          flowAfterLine: 'courseName',
        },
        {
          line: 'credential',
          x: 85,
          y: 560,
          width: 1030,
          height: 20,
          align: 'left',
          font: 'sans',
          size: 15,
          minSize: 11,
          color: SMALL_PRINT,
          maxWidth: 420,
        },
        {
          line: 'verifyShort',
          x: 85,
          y: 560,
          width: 1030,
          height: 20,
          align: 'right',
          font: 'sans',
          size: 15,
          minSize: 11,
          color: SMALL_PRINT,
          maxWidth: 420,
        },
      ],
    },
    instagram: {
      width: 1080,
      height: 1080,
      defaultScale: 2,
      background: 'certificates/aasa-2026/instagram-blank.png',
      fields: [
        {
          line: 'learnerName',
          x: 96,
          y: 565.5,
          width: 888,
          height: 120,
          align: 'center',
          font: 'name',
          size: 104,
          minSize: 41,
          color: NAME_RED,
        },
        {
          line: 'courseName',
          x: 96,
          y: 720.5,
          width: 888,
          height: 50,
          align: 'center',
          font: 'serifBold',
          size: 40,
          minSize: 30,
          color: INK,
          maxLines: 2,
        },
        {
          line: 'detailsSocial',
          x: 96,
          y: 780.5,
          width: 888,
          height: 32,
          align: 'center',
          font: 'serif',
          size: 24,
          minSize: 18,
          color: INK,
          flowAfterLine: 'courseName',
        },
        {
          line: 'credentialAndVerify',
          x: 96,
          y: 970,
          width: 888,
          height: 26,
          align: 'center',
          font: 'sans',
          size: 20,
          minSize: 14,
          color: SMALL_PRINT,
        },
      ],
    },
  },
}

const LAYOUTS: Record<string, CertificateLayout> = {'aasa-2026': AASA_2026}

export const CURRENT_CERTIFICATE_DESIGN = 'aasa-2026'

export function isCertificateDesign(templateKey: string | null | undefined): boolean {
  return !!templateKey && templateKey in LAYOUTS
}

export function certificateLayout(templateKey: string): CertificateLayout {
  const layout = LAYOUTS[templateKey]
  if (!layout) {
    throw new Error(`Unknown certificate design '${templateKey}'.`)
  }
  return layout
}
